/* eslint-disable warp-drive/no-legacy-request-patterns */
import { recordIdentifierFor } from '@warp-drive/core';

type ResourceKey = ReturnType<typeof recordIdentifierFor>;

// TS-STANDIN(@ember-data/model): the parts of a record that
// rollbackRelationships and rollbackRecord use.
// Pick<Model, ...> does not compile, because rollbackAttributes() needs a full
// record as `this`.
export interface RollbackRecord {
  readonly isDestroying: boolean;
  readonly isDestroyed: boolean;
  readonly isNew: boolean;
  readonly store: {
    readonly cache: { rollbackRelationships(key: ResourceKey): string[] };
  };
  rollbackAttributes(): void;
}

// TS-STANDIN(@ember-data/model): the parts of a record that snapshotFields
// uses.
// Model types belongsTo() and hasMany() keys per model, so a plain string key
// does not compile.
export interface SnapshotRecord {
  readonly constructor: {
    readonly relationshipsByName: ReadonlyMap<string, { kind: string }>;
  };
  belongsTo(name: string): { value(): unknown; id(): string | null };
  hasMany(name: string): { value(): { slice(): unknown[] } | null };
  get(key: string): unknown;
  set(key: string, value: unknown): unknown;
}

// rollbackRelationships discards unsaved hasMany/belongsTo edits on a record,
// and the matching edits on each inverse. rollbackAttributes() has never
// touched relationships, and since WarpDrive 5.3 a reload no longer clears
// them either: local relationship edits survive a remote update. Returns the
// names of the relationships that were reverted. An unloaded record (for
// example, after destroyRecord()) has nothing left to revert.
export function rollbackRelationships(record: RollbackRecord): string[] {
  if (record.isDestroying || record.isDestroyed) {
    return [];
  }

  return record.store.cache.rollbackRelationships(recordIdentifierFor(record));
}

// snapshotFields records the current value of each named attribute or
// relationship on a record, and returns a function that sets them back. Use
// it to undo your own edits when a save fails, without discarding unrelated
// unsaved changes the way rollbackRecord() would. A relationship that has not
// loaded yet is not restored, since there is no record to set it back to.
export function snapshotFields(
  record: SnapshotRecord,
  fields: readonly string[],
): () => void {
  const { relationshipsByName } = record.constructor;
  const snapshot: [string, unknown][] = [];
  for (const field of fields) {
    const kind = relationshipsByName.get(field)?.kind;
    if (kind === 'belongsTo') {
      const ref = record.belongsTo(field);
      const value = ref.value();
      if (value !== null || ref.id() === null) {
        snapshot.push([field, value]);
      }
    } else if (kind === 'hasMany') {
      const value = record.hasMany(field).value();
      if (value) {
        snapshot.push([field, value.slice()]);
      }
    } else {
      snapshot.push([field, record.get(field)]);
    }
  }
  return () => {
    for (const [field, value] of snapshot) {
      record.set(field, value);
    }
  };
}

// rollbackRecord discards every unsaved edit on a record: attributes,
// fragments and relationships. A new record is unloaded by
// rollbackAttributes(), so its relationships are left for that to clear.
export function rollbackRecord(record: RollbackRecord): void {
  if (!record.isNew) {
    rollbackRelationships(record);
  }
  record.rollbackAttributes();
}

// saveOrDiscard saves a record. If the save fails it rolls the record back
// (unloading a new one, so it doesn't linger in its parents' lists) and
// rethrows, leaving the caller to report the error.
export async function saveOrDiscard<R>(
  record: RollbackRecord & { save(): Promise<R> },
): Promise<R> {
  try {
    return await record.save();
  } catch (err) {
    rollbackRecord(record);
    throw err;
  }
}
